import {
  type EmailLogItem,
  NOTIFICATION_STATUSES,
  type NotificationItem,
  type NotificationStatus,
} from '@utazzo/shared';
import {
  UzBadge,
  UzButton,
  type UzColumn,
  UzDrawer,
  UzInput,
  UzPagination,
  UzSelect,
  UzTable,
} from '@utazzo/ui';
import { ExternalLink, RefreshCw } from 'lucide-react';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  useNotificationEmailLogs,
  useNotificationHistory,
  useRetryNotification,
} from '../../use-notifications';

import { NOTIFICATION_STATUS_LABEL, humanEnum } from '@/lib/enum-labels';

type View = 'engine' | 'email-logs';

const STATUS_TONE: Record<NotificationStatus, 'success' | 'warning' | 'destructive' | 'secondary'> =
  {
    PENDING: 'secondary',
    QUEUED: 'secondary',
    SENT: 'success',
    FAILED: 'destructive',
    SKIPPED: 'warning',
  };

const EMAIL_STATUS_TONE: Record<EmailLogItem['status'], 'success' | 'destructive' | 'secondary'> = {
  PENDING: 'secondary',
  SENT: 'success',
  FAILED: 'destructive',
};

const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * History tab (ADR-0049). Two views switchable via UzSelect:
 *   - engine: rows from `notifications` (driven by the engine)
 *   - email-logs: rows from `email_logs` (legacy EmailDispatcher,
 *     primarily auth flows)
 *
 * Engine view adds a tag filter (tagKey + tagValue) for the
 * "all notifications for lead X" use case via the GIN-indexed
 * `tags @> {leadId: ...}` query.
 */
export const NotificationHistoryTab = (): React.JSX.Element => {
  const [view, setView] = useState<View>('engine');

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <UzSelect
          label="View"
          value={view}
          onValueChange={(v: string) => setView(v as View)}
          options={[
            { value: 'engine', label: 'Notification engine' },
            { value: 'email-logs', label: 'Email logs (legacy)' },
          ]}
          containerClassName="w-56"
        />
        <p className="text-[11px] text-zinc-500">
          {view === 'engine'
            ? 'Rows from `notifications` written by the engine. Filter by tag for per-lead / per-customer traceability.'
            : 'Rows from `email_logs` written by the legacy EmailDispatcher (auth flows, etc.). Read-only.'}
        </p>
      </div>
      {view === 'engine' ? <EngineView /> : <EmailLogsView />}
    </div>
  );
};

const EngineView = (): React.JSX.Element => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [status, setStatus] = useState<string>('');
  const [eventCode, setEventCode] = useState<string>('');
  const [tagKey, setTagKey] = useState<string>('');
  const [tagValue, setTagValue] = useState<string>('');
  const [selected, setSelected] = useState<NotificationItem | null>(null);

  const query = {
    page,
    pageSize,
    sortDir: 'desc' as const,
    sortBy: 'createdAt',
    ...(status ? { status: status as NotificationStatus } : {}),
    ...(eventCode ? { eventCode } : {}),
    ...(tagKey && tagValue ? { tagKey: tagKey.trim(), tagValue: tagValue.trim() } : {}),
  };
  const historyQuery = useNotificationHistory(query);
  const retry = useRetryNotification();
  const data = historyQuery.data?.data ?? [];

  const columns: ReadonlyArray<UzColumn<NotificationItem>> = [
    {
      id: 'when',
      header: 'When',
      cell: (row) => (
        <span className="font-mono text-[11px] text-zinc-700">{formatDateTime(row.createdAt)}</span>
      ),
      width: '160px',
    },
    {
      id: 'event',
      header: 'Event',
      cell: (row) => <span className="font-mono text-xs text-zinc-800">{row.eventCode}</span>,
      width: '200px',
    },
    {
      id: 'channel',
      header: 'Channel',
      cell: (row) => (
        <UzBadge tone="secondary" pill>
          {row.channelCode}
        </UzBadge>
      ),
      width: '100px',
    },
    {
      id: 'recipient',
      header: 'Recipient',
      cell: (row) => (
        <span className="truncate text-xs text-zinc-700">
          {row.recipientAddress ?? (row.recipientUserId ? row.recipientUserId.slice(0, 8) : '-')}
        </span>
      ),
    },
    {
      id: 'subject',
      header: 'Subject',
      cell: (row) => <span className="truncate text-xs text-zinc-700">{row.subject ?? '-'}</span>,
    },
    {
      id: 'tags',
      header: 'Lead / Customer',
      // Surface the lead and customer tags as deep-links to their
      // respective detail pages (ADR-0049 follow-up). Other tag keys
      // fall through to static chips so we don't lose them visually.
      cell: (row) => <TagLinks tags={row.tags ?? null} />,
      width: '260px',
    },
    {
      id: 'status',
      header: 'Status',
      cell: (row) => (
        <UzBadge tone={STATUS_TONE[row.status]} pill>
          {humanEnum(NOTIFICATION_STATUS_LABEL, row.status)}
        </UzBadge>
      ),
      width: '110px',
    },
    {
      id: 'attempts',
      header: 'Tries',
      cell: (row) => <span className="font-mono text-xs text-zinc-700">{row.attempts}</span>,
      width: '60px',
      align: 'right',
    },
    {
      id: 'actions',
      header: '',
      cell: (row) => (
        <div className="flex justify-end gap-1">
          <UzButton variant="ghost" size="sm" onClick={() => setSelected(row)}>
            Details
          </UzButton>
          {row.status === 'FAILED' && (
            <UzButton
              variant="ghost"
              size="sm"
              onClick={() => retry.mutate(row.id)}
              disabled={retry.isPending}
              title="Retry"
            >
              <RefreshCw className="h-4 w-4" />
            </UzButton>
          )}
        </div>
      ),
      width: '160px',
      align: 'right',
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 p-2">
        <UzSelect
          label="Status"
          value={status}
          onValueChange={(v: string) => {
            setPage(1);
            setStatus(v);
          }}
          options={[
            { value: '', label: 'Any status' },
            ...NOTIFICATION_STATUSES.map((s) => ({ value: s, label: s })),
          ]}
          containerClassName="w-44"
        />
        <UzSelect
          label="Event"
          value={eventCode}
          onValueChange={(v: string) => {
            setPage(1);
            setEventCode(v);
          }}
          options={[
            { value: '', label: 'All events' },
            ...Array.from(new Set(data.map((r) => r.eventCode))).map((c) => ({
              value: c,
              label: c,
            })),
          ]}
          containerClassName="w-64"
        />
        <UzInput
          label="Tag key"
          placeholder="e.g. leadId"
          value={tagKey}
          onChange={(e) => setTagKey(e.target.value)}
          containerClassName="w-40"
        />
        <UzInput
          label="Tag value"
          placeholder="e.g. 019eda..."
          value={tagValue}
          onChange={(e) => setTagValue(e.target.value)}
          containerClassName="w-56"
        />
        <UzButton
          variant="outline"
          size="sm"
          onClick={() => setPage(1)}
          disabled={!tagKey || !tagValue}
        >
          Apply tag filter
        </UzButton>
        {(tagKey || tagValue) && (
          <UzButton
            variant="ghost"
            size="sm"
            onClick={() => {
              setTagKey('');
              setTagValue('');
              setPage(1);
            }}
          >
            Clear
          </UzButton>
        )}
      </div>
      <UzTable
        columns={columns}
        data={data}
        getRowId={(row) => row.id}
        loading={historyQuery.isLoading}
      />
      <UzPagination
        page={page}
        pageSize={pageSize}
        totalItems={historyQuery.data?.pagination.totalItems ?? 0}
        onPageChange={setPage}
        pageSizeOptions={[20, 50, 100]}
        onPageSizeChange={(s) => {
          setPage(1);
          setPageSize(s);
        }}
      />
      {selected && <DetailDrawer row={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

const DetailDrawer = ({
  row,
  onClose,
}: {
  row: NotificationItem;
  onClose: () => void;
}): React.JSX.Element => {
  const retry = useRetryNotification();
  return (
    <UzDrawer
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Notification ${row.id.slice(0, 8)}`}
      description={`${row.eventCode} via ${row.channelCode}`}
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={onClose}>
            Close
          </UzButton>
          {row.status === 'FAILED' && (
            <UzButton onClick={() => retry.mutate(row.id)} disabled={retry.isPending}>
              <RefreshCw className="mr-1.5 h-4 w-4" />
              Retry
            </UzButton>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Status" value={humanEnum(NOTIFICATION_STATUS_LABEL, row.status)} />
        <Field label="Attempts" value={String(row.attempts)} />
        <Field label="Created" value={formatDateTime(row.createdAt)} />
        <Field label="Queued" value={formatDateTime(row.queuedAt)} />
        <Field label="Sent" value={formatDateTime(row.sentAt)} />
        <Field label="Read" value={formatDateTime(row.readAt)} />
        <Field label="Scheduled" value={formatDateTime(row.scheduledAt)} />
        <Field label="Recipient user" value={row.recipientUserId ?? '-'} mono />
        <Field label="Recipient address" value={row.recipientAddress ?? '-'} />
        <Field label="Correlation id" value={row.correlationId ?? '-'} mono />
        <Field label="Subject" value={row.subject ?? '-'} />
        {row.tags && Object.keys(row.tags).length > 0 && (
          <div>
            <div className="text-[11px] font-medium text-zinc-500">Tags</div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {Object.entries(row.tags).map(([k, v]) => (
                <TagChip key={k} tagKey={k} tagValue={v} size="md" />
              ))}
            </div>
          </div>
        )}
        {row.skipReason && <Field label="Skipped because" value={row.skipReason} />}
        {row.errorMessage && (
          <div>
            <div className="text-[11px] font-medium text-zinc-500">Error</div>
            <pre className="rounded bg-red-50 p-2 text-xs whitespace-pre-wrap text-red-700 ring-1 ring-red-200">
              {row.errorMessage}
            </pre>
          </div>
        )}
        {row.bodyText && (
          <div>
            <div className="text-[11px] font-medium text-zinc-500">Text body (snapshot)</div>
            <pre className="rounded bg-zinc-50 p-2 text-xs whitespace-pre-wrap text-zinc-800">
              {row.bodyText}
            </pre>
          </div>
        )}
        {row.bodyHtml && (
          <div>
            <div className="text-[11px] font-medium text-zinc-500">HTML body (snapshot)</div>
            <div
              className="rounded border border-zinc-200 bg-white p-2 text-xs"
              dangerouslySetInnerHTML={{ __html: row.bodyHtml }}
            />
          </div>
        )}
      </div>
    </UzDrawer>
  );
};

const Field = ({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): React.JSX.Element => (
  <div className="flex items-baseline justify-between gap-3 border-b border-zinc-100 pb-1.5">
    <span className="text-[11px] font-medium text-zinc-500">{label}</span>
    <span className={mono ? 'font-mono text-xs text-zinc-800' : 'text-sm text-zinc-800'}>
      {value}
    </span>
  </div>
);

const EmailLogsView = (): React.JSX.Element => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [status, setStatus] = useState<string>('');
  const [templateCode, setTemplateCode] = useState<string>('');
  const [toAddress, setToAddress] = useState<string>('');

  const query = {
    page,
    pageSize,
    sortDir: 'desc' as const,
    sortBy: 'createdAt',
    ...(status ? { status: status as EmailLogItem['status'] } : {}),
    ...(templateCode ? { templateCode } : {}),
    ...(toAddress.trim() ? { toAddress: toAddress.trim() } : {}),
  };
  const emailLogsQuery = useNotificationEmailLogs(query);
  const data = emailLogsQuery.data?.data ?? [];

  const columns: ReadonlyArray<UzColumn<EmailLogItem>> = [
    {
      id: 'when',
      header: 'When',
      cell: (row) => (
        <span className="font-mono text-[11px] text-zinc-700">{formatDateTime(row.createdAt)}</span>
      ),
      width: '160px',
    },
    {
      id: 'template',
      header: 'Template code',
      cell: (row) => <span className="font-mono text-xs text-zinc-800">{row.templateCode}</span>,
      width: '200px',
    },
    {
      id: 'to',
      header: 'To',
      cell: (row) => <span className="text-xs text-zinc-700">{row.toAddress}</span>,
      width: '220px',
    },
    {
      id: 'subject',
      header: 'Subject',
      cell: (row) => <span className="truncate text-xs text-zinc-700">{row.subject}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (row) => (
        <UzBadge tone={EMAIL_STATUS_TONE[row.status]} pill>
          {humanEnum(NOTIFICATION_STATUS_LABEL, row.status)}
        </UzBadge>
      ),
      width: '110px',
    },
    {
      id: 'attempts',
      header: 'Tries',
      cell: (row) => <span className="font-mono text-xs text-zinc-700">{row.errorAttempts}</span>,
      width: '60px',
      align: 'right',
    },
    {
      id: 'sent',
      header: 'Sent',
      cell: (row) => (
        <span className="font-mono text-[11px] text-zinc-700">{formatDateTime(row.sentAt)}</span>
      ),
      width: '160px',
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 p-2">
        <UzSelect
          label="Status"
          value={status}
          onValueChange={(v: string) => {
            setPage(1);
            setStatus(v);
          }}
          options={[
            { value: '', label: 'Any status' },
            { value: 'PENDING', label: 'PENDING' },
            { value: 'SENT', label: 'SENT' },
            { value: 'FAILED', label: 'FAILED' },
          ]}
          containerClassName="w-40"
        />
        <UzInput
          label="Template code"
          placeholder="e.g. user.welcome"
          value={templateCode}
          onChange={(e) => setTemplateCode(e.target.value)}
          containerClassName="w-56"
        />
        <UzInput
          label="To address (substring)"
          placeholder="e.g. admin@"
          value={toAddress}
          onChange={(e) => setToAddress(e.target.value)}
          containerClassName="w-64"
        />
      </div>
      <UzTable
        columns={columns}
        data={data}
        getRowId={(row) => row.id}
        loading={emailLogsQuery.isLoading}
      />
      <UzPagination
        page={page}
        pageSize={pageSize}
        totalItems={emailLogsQuery.data?.pagination.totalItems ?? 0}
        onPageChange={setPage}
        pageSizeOptions={[20, 50, 100]}
        onPageSizeChange={(s) => {
          setPage(1);
          setPageSize(s);
        }}
      />
    </div>
  );
};

// ============================================================================
// Tag link helpers - deep-link leadId / customerId tags to detail pages
// ============================================================================

/**
 * Compact one-or-two-link summary used in the table's Tags column.
 * Surfaces `leadId` + `customerId` as router links (the operator's
 * primary navigation targets) and falls back to a plain key:value
 * chip for any other tag.
 *
 * Click-through stops row propagation so the surrounding row click
 * (e.g. opening the Details drawer) doesn't fire alongside the
 * navigation.
 */
const TagLinks = ({ tags }: { tags: Record<string, string> | null }): React.JSX.Element => {
  if (!tags || Object.keys(tags).length === 0)
    return <span className="text-xs text-zinc-400">-</span>;
  const leadId = tags.leadId;
  const customerId = tags.customerId;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {leadId && <TagChip tagKey="leadId" tagValue={leadId} size="sm" />}
      {customerId && <TagChip tagKey="customerId" tagValue={customerId} size="sm" />}
      {/* If there were OTHER tag keys, show how many more exist so the
          operator knows to open the detail drawer for the full picture. */}
      {Object.keys(tags).filter((k) => k !== 'leadId' && k !== 'customerId').length > 0 && (
        <span
          className="font-mono text-[10px] text-zinc-500"
          title="Open Details for the full tag map."
        >
          +{Object.keys(tags).filter((k) => k !== 'leadId' && k !== 'customerId').length}
        </span>
      )}
    </div>
  );
};

/**
 * One tag chip. When the key is a known UUID-shaped link
 * (`leadId` / `customerId`), renders as a router Link to the
 * appropriate detail page. Otherwise renders as a static badge.
 */
const TagChip = ({
  tagKey,
  tagValue,
  size,
}: {
  tagKey: string;
  tagValue: string;
  size: 'sm' | 'md';
}): React.JSX.Element => {
  const baseClass =
    size === 'sm'
      ? 'inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 font-mono text-[10px]'
      : 'inline-flex items-center gap-1 rounded px-2 py-0.5 font-mono text-[11px]';
  const truncate = (v: string): string =>
    size === 'sm' && v.length > 8 ? `${v.slice(0, 8)}...` : v;

  // Lead link
  if (tagKey === 'leadId') {
    return (
      <RouterLink
        to={`/leads/${tagValue}`}
        title={`Open lead ${tagValue}`}
        onClick={(e) => e.stopPropagation()}
        className={`${baseClass} bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200 hover:bg-indigo-100`}
      >
        <span className="text-[10px] font-semibold">lead</span>
        <span>{truncate(tagValue)}</span>
        <ExternalLink className="h-2.5 w-2.5" />
      </RouterLink>
    );
  }

  // Customer link
  if (tagKey === 'customerId') {
    return (
      <RouterLink
        to={`/customers/${tagValue}`}
        title={`Open customer ${tagValue}`}
        onClick={(e) => e.stopPropagation()}
        className={`${baseClass} bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 hover:bg-emerald-100`}
      >
        <span className="text-[10px] font-semibold">customer</span>
        <span>{truncate(tagValue)}</span>
        <ExternalLink className="h-2.5 w-2.5" />
      </RouterLink>
    );
  }

  // Fallback: plain chip
  return (
    <span className={`${baseClass} bg-zinc-100 text-zinc-700`} title={`${tagKey}=${tagValue}`}>
      <span className="font-semibold">{tagKey}</span>
      <span>{truncate(tagValue)}</span>
    </span>
  );
};
